/**
 * Animations pane: effect gallery, Effect Options and timing for the selected object, plus
 * Preview. Mirrors PowerPoint's Animations tab and its Animation Pane minus the step list —
 * ordering is implicit in the order effects were applied.
 *
 * A mask-less right-hand Drawer, the same shape as PropertiesDrawer. Applying an effect
 * means picking an object on the slide while this stays open, so it can neither cover the
 * canvas the way the old dropdown did nor dismiss itself when the canvas is clicked.
 * Overlaying rather than docking also means opening it never reflows the slide.
 */

import React, { useEffect } from 'react';
import { Drawer } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { slideAction } from '../../../store/slideSlice';
import { useSelectedObject } from '../../../hooks/useSelectedObject';
import { useStickySelection } from '../hooks/useStickySelection';
import {
  EFFECTS, EFFECT_LABELS, EFFECT_DIRECTIONS, DIRECTION_LABELS,
  TRIGGERS, TRIGGER_LABELS, BUILD_TYPES, BUILD_TYPE_LABELS,
  makeStep, findStepForTarget,
} from '../utils/animationUtils';
import classes from './AnimationsPane.module.css';

/**
 * Small inline glyph hinting at each effect's motion. Every effect draws the same base
 * rectangle plus one distinguishing mark, so the gallery reads as a set rather than a
 * collection of unrelated icons.
 */
const EffectGlyph = ({ effect }) => {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.5 };
  const marks = {
    fade: <rect x="6" y="7" width="12" height="10" rx="2" fill="currentColor" opacity="0.25" />,
    floatIn: <path d="M12 22 L12 19 M10.5 20.5 L12 19 L13.5 20.5" {...common} />,
    wipe: <path d="M6 12 L18 12" {...common} strokeDasharray="2 2" />,
    split: <path d="M12 4 L12 7 M12 17 L12 20" {...common} />,
    box: <rect x="9" y="9.5" width="6" height="5" rx="1" {...common} strokeDasharray="1.5 1.5" />,
    circle: <circle cx="12" cy="12" r="3.5" {...common} strokeDasharray="1.5 1.5" />,
    zoom: <path d="M3 12 L5.5 12 M18.5 12 L21 12" {...common} />,
    stretch: <path d="M4 12 L6 12 M6 10 L6 14 M20 12 L18 12 M18 10 L18 14" {...common} />,
    growTurn: <path d="M8.5 4.5 A5 5 0 0 1 15.5 5" {...common} />,
    flyIn: <path d="M12 22 L12 18 M9.5 20 L12 22.5 L14.5 20" {...common} />,
    bounce: <path d="M6 21 Q9 16 12 21 T18 21" {...common} />,
    swivel: <path d="M9 5.5 A6 4 0 0 0 15 5.5" {...common} />,
  };
  return (
    <svg viewBox="0 0 24 24" className={classes.glyph} aria-hidden="true">
      <rect x="6" y="7" width="12" height="10" rx="2" {...common}
        opacity={effect === 'appear' ? 1 : 0.85} />
      {marks[effect] || null}
    </svg>
  );
};

export const AnimationsPane = ({ currentSlideId }) => {
  const dispatch = useDispatch();
  const selected = useSelectedObject();
  const target = useStickySelection(selected);
  const isOpen = useSelector((state) => state.slide.animationPaneOpen);
  const close = () => dispatch(slideAction.setAnimationPaneOpen(false));

  // Escape closes the pane. There is no outside-click dismissal: clicking the slide is how
  // you choose what to animate, and the Drawer runs mask-less so those clicks reach it.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') dispatch(slideAction.setAnimationPaneOpen(false));
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, dispatch]);

  // Mask-less drawers don't get antd's page-scroll lock; see the CSS for why it is a class.
  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.classList.add('animations-scroll-lock');
    return () => document.body.classList.remove('animations-scroll-lock');
  }, [isOpen]);

  /** Keep focus on the slide so blur-driven deselection never fires for this interaction. */
  const preserveSelection = (e) => {
    const tag = e.target?.tagName;
    // Inputs and selects genuinely need focus to work; everything else does not.
    if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
    e.preventDefault();
  };

  const animations = useSelector(
    (state) => currentSlideId && state.slide.slides[currentSlideId]?.animations
  );

  const step = target ? findStepForTarget(animations, target.id) : null;
  const directions = step ? EFFECT_DIRECTIONS[step.effect] || [] : [];

  /**
   * Apply (or replace) the effect on the selected object, then play it — picking an effect
   * is how you see what it looks like, so there is no separate Preview button. This replays
   * the whole slide rather than the one object, which is what the existing preview does.
   */
  const applyEffect = (effect) => {
    if (!target || !currentSlideId) return;
    dispatch(slideAction.addAnimation({
      slideId: currentSlideId,
      step: makeStep({
        ...step,
        id: undefined, // addAnimation preserves the existing id when replacing
        targetType: target.type,
        targetId: target.id,
        effect,
      }),
    }));
    dispatch(slideAction.startAnimationPreview());
  };

  const patchStep = (patch) => {
    if (!step) return;
    // Route through makeStep so an effect change can't leave an invalid direction behind.
    dispatch(slideAction.updateAnimation({
      slideId: currentSlideId,
      stepId: step.id,
      patch: makeStep({ ...step, ...patch }),
    }));
  };

  const removeEffect = () => {
    if (!step) return;
    dispatch(slideAction.removeAnimation({ slideId: currentSlideId, stepId: step.id }));
  };

  return (
    <Drawer
      title="Animations"
      placement="right"
      width={300}
      visible={isOpen}
      onClose={close}
      // Mask-less so the canvas behind stays live: the slide is where you pick what to
      // animate, and a mask would both grey it out and swallow those clicks.
      mask={false}
      maskClosable={false}
      // .ant-drawer-body already scrolls; the contents must not be a second scroll
      // container inside it or the two fight over the same overflow.
      bodyStyle={{ padding: 12 }}
    >
      {/* Unstyled on purpose: the Drawer body owns padding and scrolling, so this is only
          a hook for the mousedown handler. */}
      <div onMouseDown={preserveSelection}>
        {!target && (
          <p className={classes.hint}>Select an object on the slide to animate it.</p>
        )}

        {target && (
          <>
            {/* Its own section, as in PowerPoint's gallery — picking None is how you take
                an animation off, so there is no separate Remove button. */}
            <div className={classes.sectionLabel}>None</div>
            <div className={classes.noneRow}>
              <button
                type="button"
                className={`${classes.effectBtn} ${classes.noneBtn} ${!step ? classes.effectBtnActive : ''}`}
                onClick={removeEffect}
                title="No animation"
              >
                <span className={classes.noneGlyph}>—</span>
                <span className={classes.effectLabel}>None</span>
              </button>
            </div>

            <div className={`${classes.sectionLabel} ${classes.sectionLabelSpaced}`}>Entrance</div>
            <div className={classes.gallery}>
              {EFFECTS.map((effect) => (
                <button
                  key={effect}
                  type="button"
                  className={`${classes.effectBtn} ${step?.effect === effect ? classes.effectBtnActive : ''}`}
                  onClick={() => applyEffect(effect)}
                  title={EFFECT_LABELS[effect]}
                >
                  <EffectGlyph effect={effect} />
                  <span className={classes.effectLabel}>{EFFECT_LABELS[effect]}</span>
                </button>
              ))}
            </div>

            {step && (
              <div className={classes.options}>
                {directions.length > 0 && (
                  <label className={classes.field}>
                    <span>Direction</span>
                    <select
                      value={step.direction || directions[0]}
                      onChange={(e) => patchStep({ direction: e.target.value })}
                    >
                      {directions.map((d) => (
                        <option key={d} value={d}>{DIRECTION_LABELS[d]}</option>
                      ))}
                    </select>
                  </label>
                )}

                {target.type === 'smartart' && (
                  <>
                    <label className={classes.field}>
                      <span>Sequence</span>
                      <select
                        value={step.buildType || 'asOneObject'}
                        onChange={(e) => patchStep({ buildType: e.target.value })}
                      >
                        {BUILD_TYPES.map((b) => (
                          <option key={b} value={b}>{BUILD_TYPE_LABELS[b]}</option>
                        ))}
                      </select>
                    </label>
                    <label className={classes.checkboxField}>
                      <input
                        type="checkbox"
                        checked={Boolean(step.reverse)}
                        onChange={(e) => patchStep({ reverse: e.target.checked })}
                      />
                      <span>Reverse order</span>
                    </label>
                  </>
                )}

                <label className={classes.field}>
                  <span>Start</span>
                  <select
                    value={step.trigger}
                    onChange={(e) => patchStep({ trigger: e.target.value })}
                  >
                    {TRIGGERS.map((t) => (
                      <option key={t} value={t}>{TRIGGER_LABELS[t]}</option>
                    ))}
                  </select>
                </label>

                <label className={classes.field}>
                  <span>Duration</span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={(step.duration / 1000).toFixed(1)}
                    onChange={(e) => patchStep({ duration: Number(e.target.value) * 1000 })}
                  />
                  <span className={classes.unit}>s</span>
                </label>

                <label className={classes.field}>
                  <span>Delay</span>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={(step.delay / 1000).toFixed(1)}
                    onChange={(e) => patchStep({ delay: Number(e.target.value) * 1000 })}
                  />
                  <span className={classes.unit}>s</span>
                </label>
              </div>
            )}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default AnimationsPane;
